/**
 * EngineConfigForm — the 30-knob config form (Cadences / Retention / Tuning
 * cards) + dirty tracking + sticky save bar (WSRPDM-0014, contract §2.2).
 *
 * Local form state is seeded ONCE from `config` via a lazy initializer, same
 * as `ConfigForm` — no prop-sync effect. `Engine.tsx` only mounts this
 * component once its `config` is loaded, so a genuine external reload gets a
 * fresh mount for free (see `useScheduleConfig`'s doc comment); a save this
 * form itself performs re-seeds `form`/`baselineForm` directly from the PUT's
 * re-read response instead of relying on a remount, so the "saved" banner
 * survives to be seen.
 *
 * **THE CRITICAL SAVE SEMANTIC (contract §2.2):** a `null` knob CLEARS it to
 * its default. `toUpdateRequest()` therefore always emits ALL 30 registry
 * knobs from current form state (never a partial/sparse body) — the edited
 * knob's new value plus the other 29 unchanged — so one edit never resets
 * the rest. The 4 enable flags are NOT part of this form (they're
 * `StartStopCard`'s, PUT individually) and are never included here.
 */
import { useState, type ReactNode } from 'react';
import { Alert, Box, Button, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { putConfig } from '@/api/wsr/scheduleConfig';
import { wsrApiError } from '@/api/wsr/errors';
import {
  COUNT_MIN_ZERO,
  INVARIANT_FIELDS,
  ISOLATION_VALUES,
  SCHEDULE_KNOBS,
  type KnobGroup,
  type ScheduleKnobKey,
} from '@/domain/scheduleKnobs';
import type { PlatformScheduleConfigUpdate, PlatformScheduleConfigView } from '@/api/wsr/types';
import { KnobGroupCard } from './KnobGroupCard';

type KnobFormState = Record<ScheduleKnobKey, string | boolean>;

export type EngineSettingsTab = 'start' | 'cadences' | 'retention' | 'tuning';

const ENGINE_TABS: readonly EngineSettingsTab[] = ['start', 'cadences', 'retention', 'tuning'];

const GROUP_TO_TAB: Record<KnobGroup, Exclude<EngineSettingsTab, 'start'>> = {
  cadence: 'cadences',
  retention: 'retention',
  tuning: 'tuning',
};

function tabOwningFirstError(
  fieldErrors: Partial<Record<ScheduleKnobKey, string>>,
  invariantMessage: string | null,
): EngineSettingsTab | null {
  const firstField = SCHEDULE_KNOBS.find((def) => fieldErrors[def.key]);
  if (firstField) return GROUP_TO_TAB[firstField.group];
  if (invariantMessage) return 'tuning';
  return null;
}

function toFormState(view: PlatformScheduleConfigView): KnobFormState {
  const state = {} as KnobFormState;
  for (const def of SCHEDULE_KNOBS) {
    const value = view[def.key as keyof PlatformScheduleConfigView];
    state[def.key] = def.unit === 'bool' ? (value as boolean) : String(value);
  }
  return state;
}

/** Always the FULL 30-knob set (see the file doc comment) — never a partial body. */
function toUpdateRequest(form: KnobFormState): PlatformScheduleConfigUpdate {
  const body: Record<string, unknown> = {};
  for (const def of SCHEDULE_KNOBS) {
    const raw = form[def.key];
    body[def.key] = def.unit === 'bool' || def.unit === 'enum' ? raw : Number(raw);
  }
  return body as PlatformScheduleConfigUpdate;
}

interface ValidationOutcome {
  fieldErrors: Partial<Record<ScheduleKnobKey, string>>;
  invariantMessage: string | null;
}

function validate(form: KnobFormState, t: ReturnType<typeof useCopy>['t']): ValidationOutcome {
  const fieldErrors: Partial<Record<ScheduleKnobKey, string>> = {};
  for (const def of SCHEDULE_KNOBS) {
    if (def.unit === 'seconds') {
      const n = Number(form[def.key]);
      if (!Number.isInteger(n) || n < 1) {
        fieldErrors[def.key] = t('engine.validation.secondsMin1', 'Must be an integer ≥ 1.');
      }
    } else if (def.unit === 'count') {
      const min = COUNT_MIN_ZERO.includes(def.key) ? 0 : 1;
      const n = Number(form[def.key]);
      if (!Number.isInteger(n) || n < min) {
        fieldErrors[def.key] =
          min === 0
            ? t('engine.validation.countMin0', 'Must be an integer ≥ 0.')
            : t('engine.validation.countMin1', 'Must be an integer ≥ 1.');
      }
    } else if (def.unit === 'enum') {
      if (!ISOLATION_VALUES.includes(form[def.key] as string)) {
        fieldErrors[def.key] = t(
          'engine.validation.isolationInvalid',
          'Must be one of the 5 supported isolation levels.',
        );
      }
    }
  }

  // Only check the cross-field invariant once all three inputs are
  // individually valid — otherwise a bad heartbeat/lease/maxExec value would
  // surface both its own field error AND a redundant invariant message.
  const [heartbeatKey, leaseKey, maxExecKey] = INVARIANT_FIELDS;
  let invariantMessage: string | null = null;
  if (!fieldErrors[heartbeatKey] && !fieldErrors[leaseKey] && !fieldErrors[maxExecKey]) {
    const heartbeat = Number(form[heartbeatKey]);
    const lease = Number(form[leaseKey]);
    const maxExec = Number(form[maxExecKey]);
    if (!(heartbeat < lease && lease < maxExec)) {
      invariantMessage = t(
        'engine.validation.invariant',
        'heartbeatIntervalSeconds must be less than leaseTtlSeconds, which must be less than maxExecutionTimeSeconds.',
      );
    }
  }

  return { fieldErrors, invariantMessage };
}

interface EngineConfigFormProps {
  config: PlatformScheduleConfigView;
  onSaved: (view: PlatformScheduleConfigView) => void;
  startStop?: ReactNode;
}

export function EngineConfigForm({ config, onSaved, startStop }: EngineConfigFormProps) {
  const { t } = useCopy();
  const [tab, setTab] = useState<EngineSettingsTab>('start');
  const [form, setForm] = useState<KnobFormState>(() => toFormState(config));
  const [baselineForm, setBaselineForm] = useState<KnobFormState>(() => toFormState(config));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<ScheduleKnobKey, string>>>({});
  const [invariantMessage, setInvariantMessage] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const invariantFields = invariantMessage ? INVARIANT_FIELDS : [];

  const dirtyKeys = SCHEDULE_KNOBS.filter((def) => form[def.key] !== baselineForm[def.key]);
  const dirtyCount = dirtyKeys.length;

  const handleChange = (key: ScheduleKnobKey, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleRevert = () => {
    setForm(baselineForm);
    setFieldErrors({});
    setInvariantMessage(null);
    setServerError(null);
    setSaved(false);
  };

  const handleSave = async () => {
    const outcome = validate(form, t);
    if (Object.keys(outcome.fieldErrors).length > 0 || outcome.invariantMessage) {
      setFieldErrors(outcome.fieldErrors);
      setInvariantMessage(outcome.invariantMessage);
      setServerError(null);
      setSaved(false);
      const errorTab = tabOwningFirstError(outcome.fieldErrors, outcome.invariantMessage);
      if (errorTab) setTab(errorTab);
      return;
    }

    setFieldErrors({});
    setInvariantMessage(null);
    setServerError(null);
    setSaving(true);
    try {
      const view = await putConfig(toUpdateRequest(form));
      const nextForm = toFormState(view);
      setForm(nextForm);
      setBaselineForm(nextForm);
      setSaved(true);
      onSaved(view);
    } catch (err) {
      const wsrErr = wsrApiError(err);
      if (wsrErr?.field) {
        const nextErrors = { [wsrErr.field as ScheduleKnobKey]: wsrErr.message };
        setFieldErrors(nextErrors);
        const errorTab = tabOwningFirstError(nextErrors, null);
        if (errorTab) setTab(errorTab);
      } else if (wsrErr) {
        // The multi-field engine invariant failure has no `field` — highlight
        // all three invariant inputs and show its message (contract §2.2).
        setInvariantMessage(wsrErr.message);
        setTab('tuning');
      } else {
        setServerError(t('engine.save.error', 'Could not save configuration. Please try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const cadenceKnobs = SCHEDULE_KNOBS.filter((def) => def.group === 'cadence');
  const retentionKnobs = SCHEDULE_KNOBS.filter((def) => def.group === 'retention');
  const tuningKnobs = SCHEDULE_KNOBS.filter((def) => def.group === 'tuning');

  return (
    <Box
      component="form"
      // The number knobs carry native `min`/`step` HTML constraints for
      // browser affordance (spinner arrows, scroll-wheel step), but native
      // HTML5 constraint validation would otherwise silently BLOCK
      // `onSubmit` from ever firing for an out-of-range value — no 'submit'
      // event, so our own validate() (which produces the spec's field-level
      // i18n message + highlight) would be dead code. `noValidate` disables
      // that native gate so our JS validation is the sole gatekeeper.
      noValidate
      aria-label={t('engine.formAriaLabel', 'Engine configuration form')}
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
      sx={{ pb: 10 }}
    >
      {serverError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {serverError}
        </Alert>
      ) : null}
      {invariantMessage ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {invariantMessage}
        </Alert>
      ) : null}
      {saved ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('engine.save.success', 'Configuration saved.')}
        </Alert>
      ) : null}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_event, value: EngineSettingsTab) => setTab(value)}
          aria-label={t('engine.tabs.ariaLabel', 'Engine settings sections')}
          variant="scrollable"
          scrollButtons="auto"
        >
          {ENGINE_TABS.map((key) => (
            <Tab
              key={key}
              value={key}
              label={
                key === 'start'
                  ? t('engine.flags.title', 'Start / stop')
                  : key === 'cadences'
                    ? t('engine.group.cadence', 'Cadences')
                    : key === 'retention'
                      ? t('engine.group.retention', 'Retention')
                      : t('engine.group.tuning', 'Tuning')
              }
              id={`engine-tab-${key}`}
              aria-controls={`engine-panel-${key}`}
            />
          ))}
        </Tabs>
      </Box>

      <Box
        id="engine-panel-start"
        role="tabpanel"
        hidden={tab !== 'start'}
        aria-labelledby="engine-tab-start"
      >
        {startStop}
      </Box>
      <Box
        id="engine-panel-cadences"
        role="tabpanel"
        hidden={tab !== 'cadences'}
        aria-labelledby="engine-tab-cadences"
      >
        <KnobGroupCard
          knobs={cadenceKnobs}
          form={form}
          fieldErrors={fieldErrors}
          invariantFields={invariantFields}
          onChange={handleChange}
        />
      </Box>
      <Box
        id="engine-panel-retention"
        role="tabpanel"
        hidden={tab !== 'retention'}
        aria-labelledby="engine-tab-retention"
      >
        <KnobGroupCard
          knobs={retentionKnobs}
          form={form}
          fieldErrors={fieldErrors}
          invariantFields={invariantFields}
          onChange={handleChange}
        />
      </Box>
      <Box
        id="engine-panel-tuning"
        role="tabpanel"
        hidden={tab !== 'tuning'}
        aria-labelledby="engine-tab-tuning"
      >
        <KnobGroupCard
          knobs={tuningKnobs}
          form={form}
          fieldErrors={fieldErrors}
          invariantFields={invariantFields}
          onChange={handleChange}
        />
      </Box>

      <Paper
        elevation={3}
        sx={{
          position: 'sticky',
          bottom: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {dirtyCount > 0
            ? t('engine.save.changedCount', '{{count}} changed', { count: dirtyCount })
            : t('engine.save.noChanges', 'No changes')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={handleRevert} disabled={dirtyCount === 0 || saving}>
            {t('engine.save.revert', 'Revert')}
          </Button>
          <Button type="submit" variant="contained" disabled={dirtyCount === 0 || saving}>
            {saving
              ? t('engine.save.saving', 'Saving…')
              : t('engine.save.saveAll', 'Save all settings')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
